import { useQuery } from "@apollo/client";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  Autocomplete,
  Button,
  Dialog,
  IconButton,
  Skeleton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { subDays, subMonths } from "date-fns";
import styled from "styled-components";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import StatCard from "../../components/StatCard/StatCard";
import { SubscriptionsResult } from "../../graphql/graphql";
import { SUBSCRIPTIONS } from "../../graphql/queries-gql";
import { CHART_SERIES_COLORS, useChartTheme } from "../../theme/chartTheme";

type AgeCount = {
  age: number;
  count: number;
};

const COLORS = CHART_SERIES_COLORS;

// One fixed color per breakdown, shared by the card chart and its "View all" dialog
const SERIES = {
  age: CHART_SERIES_COLORS[0],
  countries: CHART_SERIES_COLORS[1],
  ads: CHART_SERIES_COLORS[2],
  keywords: CHART_SERIES_COLORS[5],
  adCountries: CHART_SERIES_COLORS[9],
  countryKeywords: CHART_SERIES_COLORS[0],
};

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

// Panel has no click handler, so clickable chart cards wrap it. Keyboard users
// get the same action through the "View all" button in the panel header.
const ClickableCard = styled.div`
  cursor: pointer;
  min-width: 0;

  > section {
    height: 100%;
    transition: border-color 0.15s ease;
  }

  &:hover > section {
    border-color: ${(props) => props.theme.borderStrong};
  }
`;

const ChartContainer = styled.div<{ $height?: number }>`
  width: 100%;
  height: ${(props) => props.$height ?? 300}px;
  flex-shrink: 0;
`;

const ChartEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 1px dashed ${(props) => props.theme.borderStrong};
  border-radius: 8px;
  font-size: 13px;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const FilterLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
`;

const CountrySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DialogHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${(props) => props.theme.border};
`;

const DialogTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const DialogHeading = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
`;

const DialogBody = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 20px 20px;
`;

const CloseIcon = styled(XMarkIcon)`
  width: 20px;
  height: 20px;
`;

const DataList = styled.div`
  display: flex;
  flex-direction: column;
`;

const DataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 8px;
  border-bottom: 1px solid ${(props) => props.theme.border};
  border-radius: 6px;
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${(props) => props.theme.surfaceHover};
  }
`;

const DataName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 13.5px;
  color: ${(props) => props.theme.textColor};
  overflow-wrap: anywhere;
`;

const Swatch = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => props.$color};
`;

const DataCount = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.textColor};
`;

const Subscriptions = () => {
  const theme = useTheme();
  const chart = useChartTheme();
  const [selectedData, setSelectedData] = useState<any>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [showTrials, setShowTrials] = useState<"all" | "trials" | "no-trials">(
    "all"
  );
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [keywordSearch, setKeywordSearch] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [datePreset, setDatePreset] = useState<string | null>(null);

  const setDateRange = (preset: string) => {
    const now = new Date();
    setDatePreset(preset);

    switch (preset) {
      case "30days":
        setStartDate(subDays(now, 30));
        setEndDate(now);
        break;
      case "90days":
        setStartDate(subDays(now, 90));
        setEndDate(now);
        break;
      case "12months":
        setStartDate(subMonths(now, 12));
        setEndDate(now);
        break;
      case "clear":
        setStartDate(null);
        setEndDate(null);
        setDatePreset(null);
        break;
    }
  };

  const { data } = useQuery<{
    subscriptions: SubscriptionsResult;
  }>(SUBSCRIPTIONS, {
    fetchPolicy: "cache-first",
    variables: {
      args: {
        showActiveOnly,
        ...(startDate && { startDate: startDate.toISOString() }),
        ...(endDate && { endDate: endDate.toISOString() }),
      },
    },
    notifyOnNetworkStatusChange: false,
    onCompleted: (data) => {
      console.log("data", JSON.stringify(data, null, 2));
    },
  });

  const filteredData = useMemo(() => {
    if (!data?.subscriptions) return null;

    const filterTrialData = (items: any[]) => {
      if (showTrials === "all") return items;
      return items.filter((item) =>
        showTrials === "trials" ? item.trial : !item.trial
      );
    };

    const filteredAdAttributions = filterTrialData(
      data.subscriptions.analyticData.adAttributions
    );

    // For sources, we need to keep the original data since it's not tied to trial status
    const sources = data.subscriptions.analyticData.sources;

    return {
      ...data.subscriptions,
      data: data.subscriptions.data
        ? filterTrialData(data.subscriptions.data)
        : null,
      analyticData: {
        ...data.subscriptions.analyticData,
        adAttributions: filteredAdAttributions,
        sources: sources,
        countries: data.subscriptions.analyticData.countries,
      },
    };
  }, [data?.subscriptions, showTrials]);

  const ageData = useMemo(() => {
    if (!filteredData?.data) return [];

    const ageCount: AgeCount[] = Array.from({ length: 33 }, (_, i) => ({
      age: i + 18,
      count: 0,
    }));

    filteredData.data.forEach((user) => {
      if (user.age && user.age >= 18 && user.age <= 50) {
        const index = user.age - 18;
        if (index >= 0 && index < ageCount.length) {
          ageCount[index].count++;
        }
      }
    });

    return ageCount;
  }, [filteredData?.data]);

  const stats = useMemo(() => {
    if (!filteredData) {
      return {
        totalSubscriptions: 0,
        totalCountries: 0,
        totalAdGroups: 0,
      };
    }

    const getTotalSubscriptions = () => {
      if (showTrials === "trials") {
        return filteredData.totalTrials || 0;
      }

      // For "No Trials", subtract trials from total
      if (showTrials === "no-trials") {
        const total = filteredData.analyticData.sources.reduce(
          (acc, curr) => acc + curr.count,
          0
        );
        const trials = filteredData.totalTrials || 0;
        return total - trials;
      }

      // For "All", show total from sources
      return filteredData.analyticData.sources.reduce(
        (acc, curr) => acc + curr.count,
        0
      );
    };

    return {
      totalSubscriptions: getTotalSubscriptions(),
      totalCountries: filteredData.analyticData.countries.length,
      totalAdGroups: filteredData.analyticData.adAttributions.length,
    };
  }, [filteredData, showTrials]);

  const sortedSources = useMemo(() => {
    if (!filteredData) return [];
    return [...filteredData.analyticData.sources].sort(
      (a, b) => b.count - a.count
    );
  }, [filteredData?.analyticData.sources]);

  const sortedCountries = useMemo(() => {
    if (!filteredData) return [];
    return [...filteredData.analyticData.countries].sort(
      (a, b) => b.count - a.count
    );
  }, [filteredData?.analyticData.countries]);

  const sortedAdAttributions = useMemo(() => {
    if (!filteredData) return [];

    // Group by adGroup and count occurrences, excluding N/A
    const adGroupCounts = new Map<string, number>();
    filteredData.analyticData.adAttributions
      .filter((ad) => ad.adGroup && ad.adGroup !== "N/A")
      .forEach((ad) => {
        adGroupCounts.set(ad.adGroup, (adGroupCounts.get(ad.adGroup) || 0) + 1);
      });

    return Array.from(adGroupCounts.entries())
      .map(([adGroup, count]) => ({ adGroup, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredData?.analyticData.adAttributions]);

  const sortedKeywords = useMemo(() => {
    if (!filteredData) return [];

    const keywordCounts = new Map<string, number>();

    filteredData.analyticData.adAttributions
      .filter((ad) => ad.keyword && ad.keyword !== "N/A")
      .forEach((ad) => {
        keywordCounts.set(ad.keyword, (keywordCounts.get(ad.keyword) || 0) + 1);
      });

    return Array.from(keywordCounts.entries())
      .map(([keyword, count]) => ({
        keyword,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [filteredData?.analyticData.adAttributions]);

  const sortedAdCountries = useMemo(() => {
    if (!filteredData) return [];

    const countryCounts = new Map<string, number>();

    filteredData.analyticData.adAttributions
      .filter(
        (ad) =>
          ad.country &&
          ad.adGroup &&
          ad.adGroup !== "N/A" &&
          ad.keyword &&
          ad.keyword !== "N/A"
      )
      .forEach((ad) => {
        countryCounts.set(ad.country, (countryCounts.get(ad.country) || 0) + 1);
      });

    return Array.from(countryCounts.entries())
      .map(([country, count]) => ({
        name: country,
        count: count,
      }))
      .sort((a, b) => b.count - a.count)
      .filter((country) => country.count > 0);
  }, [filteredData?.analyticData.adAttributions]);

  const countryKeywordData = useMemo(() => {
    if (!filteredData || !selectedCountry) return [];

    const keywordCounts = new Map<string, number>();

    filteredData.analyticData.adAttributions
      .filter(
        (ad) =>
          ad.country === selectedCountry &&
          ad.keyword &&
          ad.keyword !== "N/A" &&
          ad.adGroup &&
          ad.adGroup !== "N/A"
      )
      .forEach((ad) => {
        keywordCounts.set(ad.keyword, (keywordCounts.get(ad.keyword) || 0) + 1);
      });

    return Array.from(keywordCounts.entries())
      .map(([keyword, count]) => ({
        keyword,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [filteredData?.analyticData.adAttributions, selectedCountry]);

  const availableCountries = useMemo(() => {
    if (!filteredData) return [];

    const countries = new Set<string>();
    filteredData.analyticData.adAttributions.forEach((ad) => {
      if (ad.country) {
        countries.add(ad.country);
      }
    });

    return Array.from(countries).sort();
  }, [filteredData?.analyticData.adAttributions]);

  useEffect(() => {
    if (availableCountries.length > 0 && !selectedCountry) {
      // Try to set BR as default, otherwise use first available country
      const brIndex = availableCountries.indexOf("BR");
      setSelectedCountry(brIndex !== -1 ? "BR" : availableCountries[0]);
    }
  }, [availableCountries, selectedCountry]);

  const handleCardClick = (cardId: string, data: any) => {
    if (cardId === "age") return; // Don't open popup for age distribution
    setSelectedType(cardId);
    setSelectedData(data);
  };

  // Get top 10 for display in the main view
  const topTenCountries = useMemo(
    () => sortedCountries.slice(0, 10),
    [sortedCountries]
  );
  const topTenAdGroups = useMemo(
    () => sortedAdAttributions.slice(0, 10),
    [sortedAdAttributions]
  );
  const topTenKeywords = useMemo(
    () => sortedKeywords.slice(0, 10),
    [sortedKeywords]
  );
  const topTenAdCountries = useMemo(
    () => sortedAdCountries.slice(0, 10),
    [sortedAdCountries]
  );

  const filteredModalData = useMemo(() => {
    if (!selectedData || !keywordSearch) return selectedData;

    const searchTerm = keywordSearch.toLowerCase();
    return selectedData.filter((item: any) => {
      const keyword =
        item.keyword || item.adGroup || item.country || item.source || "";
      return keyword.toLowerCase().includes(searchTerm);
    });
  }, [selectedData, keywordSearch]);

  const closeDialog = () => {
    setSelectedData(null);
    setSelectedType(null);
    setKeywordSearch("");
  };

  const barTooltip = {
    ...chart.tooltip,
    contentStyle: { ...chart.tooltip.contentStyle, zIndex: 1000 },
    cursor: { fill: theme.surfaceHover },
  };

  const legendFormatter = (maxLength?: number) => (value: string) =>
    (
      <span style={{ color: theme.secondaryTextColor, fontSize: "12px" }}>
        {maxLength !== undefined && value.length > maxLength
          ? `${value.substring(0, maxLength)}...`
          : value}
      </span>
    );

  const truncate = (maxLength: number) => (value: string) =>
    value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;

  const renderChartEmpty = () => <ChartEmpty>No data for these filters</ChartEmpty>;

  const renderVerticalBarChart = ({
    data,
    dataKey,
    color,
    left,
    width,
    fontSize,
    maxLength,
    labelKey,
    onBarClick,
  }: {
    data: { count: number }[];
    dataKey: string;
    color: string;
    left: number;
    width: number;
    fontSize: number;
    maxLength: number;
    // Show this payload field as the tooltip row name instead of "count"
    labelKey?: string;
    onBarClick?: (data: any) => void;
  }) => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 5, right: 5, bottom: 5, left }}
      >
        <CartesianGrid {...chart.grid} vertical horizontal={false} />
        <XAxis type="number" {...chart.axis} />
        <YAxis
          type="category"
          dataKey={dataKey}
          {...chart.axis}
          tick={{ fontSize }}
          width={width}
          tickFormatter={truncate(maxLength)}
        />
        <Tooltip
          {...barTooltip}
          {...(labelKey && {
            formatter: (value: number, name: string, props: any) => [
              value,
              props.payload[labelKey],
            ],
          })}
        />
        <Bar
          dataKey="count"
          fill={color}
          radius={[0, 3, 3, 0]}
          {...(onBarClick && { onClick: onBarClick, cursor: "pointer" })}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderViewAll = (onClick: () => void) => (
    <Button
      variant="text"
      color="inherit"
      size="small"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      View all
    </Button>
  );

  const renderFilters = (disabled: boolean) => (
    <>
      <FilterGroup>
        <FilterLabel>Trial status</FilterLabel>
        <ToggleButtonGroup
          value={showTrials}
          exclusive
          size="small"
          disabled={disabled}
          onChange={(_, newValue) => {
            if (newValue !== null) {
              setShowTrials(newValue);
            }
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="trials">Trials only</ToggleButton>
          <ToggleButton value="no-trials">No trials</ToggleButton>
        </ToggleButtonGroup>
      </FilterGroup>

      <FilterGroup>
        <FilterLabel>Date range</FilterLabel>
        <ToggleButtonGroup
          value={datePreset}
          exclusive
          size="small"
          disabled={disabled}
        >
          <ToggleButton value="30days" onClick={() => setDateRange("30days")}>
            Last 30 days
          </ToggleButton>
          <ToggleButton value="90days" onClick={() => setDateRange("90days")}>
            Last 90 days
          </ToggleButton>
          <ToggleButton
            value="12months"
            onClick={() => setDateRange("12months")}
          >
            Last 12 months
          </ToggleButton>
        </ToggleButtonGroup>
        <TextField
          label="Start date"
          type="date"
          size="small"
          disabled={disabled}
          value={startDate ? startDate.toISOString().split("T")[0] : ""}
          onChange={(e) => {
            setStartDate(e.target.value ? new Date(e.target.value) : null);
            setDatePreset(null);
          }}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ width: 160 }}
        />
        <TextField
          label="End date"
          type="date"
          size="small"
          disabled={disabled}
          value={endDate ? endDate.toISOString().split("T")[0] : ""}
          onChange={(e) => {
            setEndDate(e.target.value ? new Date(e.target.value) : null);
            setDatePreset(null);
          }}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ width: 160 }}
        />
        {(startDate || endDate) && (
          <Button
            variant="text"
            color="inherit"
            size="small"
            disabled={disabled}
            onClick={() => setDateRange("clear")}
          >
            Clear
          </Button>
        )}
      </FilterGroup>
    </>
  );

  const renderShowToggle = (disabled: boolean) => (
    <ToggleButtonGroup
      value={showActiveOnly}
      exclusive
      size="small"
      disabled={disabled}
      aria-label="Show"
      onChange={(_, newValue) => {
        if (newValue !== null) {
          setShowActiveOnly(newValue);
        }
      }}
    >
      <ToggleButton value={true}>Active users</ToggleButton>
      <ToggleButton value={false}>Historical users</ToggleButton>
    </ToggleButtonGroup>
  );

  const pageDescription =
    "Subscriptions broken down by age, source, country and ad attribution.";

  if (!filteredData) {
    return (
      <Page
        title="Subscriptions"
        description={pageDescription}
        maxWidth={1400}
        actions={renderShowToggle(true)}
        toolbar={renderFilters(true)}
      >
        <SummaryGrid>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={96} />
          ))}
        </SummaryGrid>

        <Grid>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} variant="rounded" height={372} />
          ))}
        </Grid>

        <CountrySection>
          <Skeleton variant="rounded" width={260} height={40} />
          <Grid>
            <Skeleton variant="rounded" height={372} />
            <Skeleton variant="rounded" height={372} />
          </Grid>
        </CountrySection>
      </Page>
    );
  }

  return (
    <Page
      title="Subscriptions"
      description={pageDescription}
      maxWidth={1400}
      actions={renderShowToggle(false)}
      toolbar={renderFilters(false)}
    >
      <SummaryGrid>
        <StatCard
          label="Total subscriptions"
          value={stats.totalSubscriptions}
        />
        <StatCard label="Active countries" value={stats.totalCountries} />
        <StatCard label="Ad groups" value={stats.totalAdGroups} />
      </SummaryGrid>

      <Grid>
        <Panel title="Age distribution">
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={ageData}
                margin={{ top: 5, right: 5, bottom: 20, left: 25 }}
              >
                <CartesianGrid {...chart.grid} />
                <XAxis dataKey="age" {...chart.axis} interval={2} />
                <YAxis {...chart.axis} tickCount={6} />
                <Tooltip {...barTooltip} />
                <Bar dataKey="count" fill={SERIES.age} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Panel>

        <ClickableCard
          onClick={() => handleCardClick("sources", sortedSources)}
        >
          <Panel
            title="Subscription sources"
            actions={renderViewAll(() =>
              handleCardClick("sources", sortedSources)
            )}
          >
            <ChartContainer>
              {sortedSources.length === 0 ? (
                renderChartEmpty()
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sortedSources}
                      dataKey="count"
                      nameKey="source"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      label={false}
                      stroke={theme.cardBg}
                    >
                      {sortedSources.map((_, index: number) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip {...chart.tooltip} />
                    <Legend
                      {...chart.legend}
                      verticalAlign="bottom"
                      height={36}
                      formatter={legendFormatter(15)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartContainer>
          </Panel>
        </ClickableCard>

        <ClickableCard
          onClick={() => handleCardClick("countries", sortedCountries)}
        >
          <Panel
            title="Top 10 countries"
            actions={renderViewAll(() =>
              handleCardClick("countries", sortedCountries)
            )}
          >
            <ChartContainer>
              {topTenCountries.length === 0
                ? renderChartEmpty()
                : renderVerticalBarChart({
                    data: topTenCountries,
                    dataKey: "country",
                    color: SERIES.countries,
                    left: 80,
                    width: 75,
                    fontSize: 12,
                    maxLength: 12,
                  })}
            </ChartContainer>
          </Panel>
        </ClickableCard>

        <ClickableCard
          onClick={() => handleCardClick("ads", sortedAdAttributions)}
        >
          <Panel
            title="Top 10 ad groups"
            actions={renderViewAll(() =>
              handleCardClick("ads", sortedAdAttributions)
            )}
          >
            <ChartContainer>
              {topTenAdGroups.length === 0
                ? renderChartEmpty()
                : renderVerticalBarChart({
                    data: topTenAdGroups,
                    dataKey: "adGroup",
                    color: SERIES.ads,
                    left: 100,
                    width: 95,
                    fontSize: 11,
                    maxLength: 25,
                  })}
            </ChartContainer>
          </Panel>
        </ClickableCard>

        <ClickableCard
          onClick={() => handleCardClick("keywords", sortedKeywords)}
        >
          <Panel
            title="Top 10 keywords"
            actions={renderViewAll(() =>
              handleCardClick("keywords", sortedKeywords)
            )}
          >
            <ChartContainer>
              {topTenKeywords.length === 0
                ? renderChartEmpty()
                : renderVerticalBarChart({
                    data: topTenKeywords,
                    dataKey: "keyword",
                    color: SERIES.keywords,
                    left: 100,
                    width: 95,
                    fontSize: 11,
                    maxLength: 25,
                    labelKey: "keyword",
                  })}
            </ChartContainer>
          </Panel>
        </ClickableCard>

        {/* Not clickable as a whole: clicking a bar selects that country below */}
        <Panel
          title="Top 10 countries by ad attribution"
          description="Click a bar to show its keywords below."
          actions={renderViewAll(() =>
            handleCardClick("adCountries", sortedAdCountries)
          )}
        >
          <ChartContainer>
            {topTenAdCountries.length === 0
              ? renderChartEmpty()
              : renderVerticalBarChart({
                  data: topTenAdCountries,
                  dataKey: "name",
                  color: SERIES.adCountries,
                  left: 100,
                  width: 95,
                  fontSize: 12,
                  maxLength: 20,
                  labelKey: "name",
                  onBarClick: (data: any) => {
                    if (data && data.name) {
                      setSelectedCountry(data.name);
                    }
                  },
                })}
          </ChartContainer>
        </Panel>
      </Grid>

      <CountrySection>
        <SectionHeader>
          <SectionTitle>Keyword performance by country</SectionTitle>
          <Autocomplete
            value={selectedCountry}
            onChange={(_, newValue) => setSelectedCountry(newValue || "")}
            options={availableCountries}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search country..."
                variant="outlined"
              />
            )}
            disableClearable
            size="small"
            sx={{ width: 240 }}
          />
        </SectionHeader>

        <Grid>
          <Panel title={`Top keywords for ${selectedCountry}`}>
            <ChartContainer>
              {countryKeywordData.length === 0
                ? renderChartEmpty()
                : renderVerticalBarChart({
                    data: countryKeywordData.slice(0, 10),
                    dataKey: "keyword",
                    color: SERIES.countryKeywords,
                    left: 100,
                    width: 95,
                    fontSize: 11,
                    maxLength: 25,
                    labelKey: "keyword",
                  })}
            </ChartContainer>
          </Panel>

          <Panel title={`Keyword distribution for ${selectedCountry}`}>
            <ChartContainer>
              {countryKeywordData.length === 0 ? (
                renderChartEmpty()
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={countryKeywordData.slice(0, 10)}
                      dataKey="count"
                      nameKey="keyword"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      label
                      stroke={theme.cardBg}
                    >
                      {countryKeywordData.slice(0, 10).map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip {...chart.tooltip} />
                    <Legend
                      {...chart.legend}
                      verticalAlign="bottom"
                      height={36}
                      formatter={legendFormatter(15)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartContainer>
          </Panel>
        </Grid>
      </CountrySection>

      <Dialog
        open={Boolean(selectedData)}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: "90vh", display: "flex", flexDirection: "column" },
        }}
      >
        {selectedData && (
          <>
            <DialogHeader>
              <DialogTitleRow>
                <DialogHeading>
                  {selectedType === "sources" && "Subscription sources"}
                  {selectedType === "countries" && "All countries"}
                  {selectedType === "ads" && "All ad groups"}
                  {selectedType === "keywords" && "All keywords"}
                  {selectedType === "adCountries" &&
                    "All countries by ad attribution"}
                </DialogHeading>
                <MuiTooltip title="Close">
                  <IconButton
                    size="small"
                    aria-label="Close"
                    onClick={closeDialog}
                  >
                    <CloseIcon />
                  </IconButton>
                </MuiTooltip>
              </DialogTitleRow>

              {(selectedType === "keywords" || selectedType === "ads") && (
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Search keywords..."
                  value={keywordSearch}
                  onChange={(e) => setKeywordSearch(e.target.value)}
                />
              )}
            </DialogHeader>

            <DialogBody>
              <ChartContainer $height={400}>
                <ResponsiveContainer width="100%" height="100%">
                  {selectedType === "sources" ? (
                    <PieChart>
                      <Pie
                        data={filteredModalData}
                        dataKey="count"
                        nameKey="source"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        label
                        stroke={theme.cardBg}
                      >
                        {filteredModalData.map((_: any, index: number) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip {...chart.tooltip} />
                      <Legend
                        {...chart.legend}
                        verticalAlign="bottom"
                        height={36}
                        formatter={legendFormatter()}
                      />
                    </PieChart>
                  ) : (
                    <BarChart
                      data={filteredModalData}
                      layout="vertical"
                      margin={{ top: 20, right: 20, bottom: 20, left: 150 }}
                    >
                      <CartesianGrid
                        {...chart.grid}
                        vertical
                        horizontal={false}
                      />
                      <XAxis type="number" {...chart.axis} />
                      <YAxis
                        type="category"
                        dataKey={
                          selectedType === "countries"
                            ? "country"
                            : selectedType === "adCountries"
                            ? "name"
                            : selectedType === "keywords"
                            ? "keyword"
                            : "adGroup"
                        }
                        {...chart.axis}
                        width={145}
                      />
                      <Tooltip {...barTooltip} />
                      <Bar
                        dataKey="count"
                        radius={[0, 3, 3, 0]}
                        fill={
                          selectedType === "countries"
                            ? SERIES.countries
                            : selectedType === "ads"
                            ? SERIES.ads
                            : selectedType === "adCountries"
                            ? SERIES.adCountries
                            : SERIES.keywords
                        }
                      />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </ChartContainer>
              <DataList>
                {filteredModalData.map((item: any, index: number) => (
                  <DataRow key={index}>
                    <DataName>
                      {selectedType === "sources" && (
                        <Swatch $color={COLORS[index % COLORS.length]} />
                      )}
                      {item.source ||
                        item.name ||
                        item.country ||
                        item.adGroup ||
                        item.keyword}
                    </DataName>
                    <DataCount>{item.count}</DataCount>
                  </DataRow>
                ))}
              </DataList>
            </DialogBody>
          </>
        )}
      </Dialog>
    </Page>
  );
};

export default Subscriptions;
